/**
 * GbpProfilesPanel — admin CRUD for Google Business Profiles.
 *
 * Endpoints used:
 *   GET   /api/admin/gbp                       — list all (incl. inactive)
 *   POST  /api/admin/gbp                       — create
 *   PUT   /api/admin/gbp/{city_slug}           — update
 *   PATCH /api/admin/gbp/{city_slug}/toggle    — flip active flag
 *   DELETE /api/admin/gbp/{city_slug}          — remove
 *
 * UI:
 *   - Table view of all city profiles with name, slug, status pill, edit/toggle/delete
 *   - Inline "+ Add city" button opens the create form
 *   - Click-to-edit row opens the same form pre-filled
 *   - Validation: slug must be lowercase-kebab-case; URLs must be Google-owned
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { invalidateGbpCache } from "../../lib/useGbp";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ExternalLink, MapPin, Star, Globe, Power, Search, Loader2 } from "lucide-react";

const EMPTY_FORM = {
    city_slug: "",
    city_name: "",
    business_url: "",
    map_url: "",
    review_url: "",
    kgmid: "",
    active: true,
};

const VALID_HOSTS = [
    "google.com", "google.co.in", "share.google", "goo.gl",
    "maps.app.goo.gl", "g.co", "maps.google.com",
];

function isValidGoogleUrl(url) {
    if (!url) return true; // optional
    const u = String(url).trim().toLowerCase();
    if (!(u.startsWith("https://") || u.startsWith("http://"))) return false;
    return VALID_HOSTS.some((h) => u.includes(h));
}

function slugify(str) {
    return String(str || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80);
}

export default function GbpProfilesPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null); // null | "new" | "<slug>"
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/gbp");
            setItems(Array.isArray(r.data) ? r.data : []);
        } catch {
            toast.error("Failed to load GBP profiles");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openNew = () => {
        setEditing("new");
        setForm(EMPTY_FORM);
    };

    const openEdit = (row) => {
        setEditing(row.city_slug);
        setForm({
            city_slug: row.city_slug,
            city_name: row.city_name || "",
            business_url: row.business_url || "",
            map_url: row.map_url || "",
            review_url: row.review_url || "",
            kgmid: row.kgmid || "",
            active: row.active !== false,
        });
    };

    const closeForm = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
    };

    const validateAll = () => {
        if (!form.city_slug.trim()) return "City slug is required (lowercase, kebab-case).";
        if (!/^[a-z][a-z0-9-]{0,79}$/.test(form.city_slug.trim())) {
            return "City slug must be lowercase letters, digits and hyphens only (e.g. 'bhopal', 'new-delhi').";
        }
        if (!form.city_name.trim()) return "City name is required.";
        for (const f of ["business_url", "map_url", "review_url"]) {
            if (form[f] && !isValidGoogleUrl(form[f])) {
                return `${f.replace("_", " ")} must be a valid Google URL (google.com, share.google, goo.gl, etc).`;
            }
        }
        return null;
    };

    const save = async () => {
        const err = validateAll();
        if (err) { toast.error(err); return; }
        setSaving(true);
        try {
            if (editing === "new") {
                await api.post("/admin/gbp", form);
                toast.success(`Added ${form.city_name} GBP`);
            } else {
                const { city_slug, ...rest } = form;
                await api.put(`/admin/gbp/${city_slug}`, rest);
                toast.success(`Updated ${form.city_name} GBP`);
            }
            invalidateGbpCache();
            closeForm();
            await load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Save failed";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (row) => {
        try {
            await api.patch(`/admin/gbp/${row.city_slug}/toggle`);
            invalidateGbpCache();
            toast.success(`${row.city_name} is now ${row.active ? "inactive" : "active"}.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Toggle failed");
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete the GBP entry for ${row.city_name}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/gbp/${row.city_slug}`);
            invalidateGbpCache();
            toast.success(`Deleted ${row.city_name} GBP`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const filtered = items.filter((r) =>
        !search.trim() ||
        r.city_name?.toLowerCase().includes(search.toLowerCase()) ||
        r.city_slug?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <section data-testid="admin-gbp-panel" className="space-y-6">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="text-xl font-heading font-bold text-slate-900">Google Business Profiles</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Add, edit, enable or remove the Google listing for each city. Changes appear on the public site automatically.</p>
                </div>
                <button
                    onClick={openNew}
                    data-testid="admin-gbp-add"
                    className="inline-flex items-center gap-2 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold px-4 py-2 rounded-lg text-sm transition-colors"
                >
                    <Plus className="w-4 h-4" /> Add city profile
                </button>
            </header>

            <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                    type="text"
                    placeholder="Search by city name or slug…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="admin-gbp-search"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 outline-none"
                />
            </div>

            <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">City</th>
                            <th className="text-left px-4 py-3">Slug</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-left px-4 py-3">Links</th>
                            <th className="text-right px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading && (
                            <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                            </td></tr>
                        )}
                        {!loading && filtered.length === 0 && (
                            <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                                No GBP profiles yet. Click "Add city profile" to create one.
                            </td></tr>
                        )}
                        {!loading && filtered.map((row) => (
                            <tr key={row.city_slug} data-testid={`admin-gbp-row-${row.city_slug}`} className="hover:bg-slate-50/60">
                                <td className="px-4 py-3 font-semibold text-slate-900">{row.city_name}</td>
                                <td className="px-4 py-3 text-slate-500 font-mono text-xs">/{row.city_slug}</td>
                                <td className="px-4 py-3">
                                    <button
                                        onClick={() => toggleActive(row)}
                                        data-testid={`admin-gbp-toggle-${row.city_slug}`}
                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${row.active
                                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                            : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                                    >
                                        <Power className="w-3 h-3" /> {row.active ? "Active" : "Inactive"}
                                    </button>
                                </td>
                                <td className="px-4 py-3 space-x-2">
                                    {row.business_url && (
                                        <a href={row.business_url} target="_blank" rel="noopener noreferrer"
                                           className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline" title="Business URL">
                                            <Globe className="w-3.5 h-3.5" /> Business
                                        </a>
                                    )}
                                    {row.map_url && (
                                        <a href={row.map_url} target="_blank" rel="noopener noreferrer"
                                           className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline" title="Map URL">
                                            <MapPin className="w-3.5 h-3.5" /> Map
                                        </a>
                                    )}
                                    {row.review_url && (
                                        <a href={row.review_url} target="_blank" rel="noopener noreferrer"
                                           className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline" title="Review URL">
                                            <Star className="w-3.5 h-3.5" /> Reviews
                                        </a>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button onClick={() => openEdit(row)} data-testid={`admin-gbp-edit-${row.city_slug}`}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded">
                                        <Pencil className="w-3.5 h-3.5" /> Edit
                                    </button>
                                    <button onClick={() => remove(row)} data-testid={`admin-gbp-delete-${row.city_slug}`}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded ml-1">
                                        <Trash2 className="w-3.5 h-3.5" /> Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Add / Edit modal */}
            {editing && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="admin-gbp-form">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                            <h3 className="font-heading font-bold text-slate-900">
                                {editing === "new" ? "Add city GBP" : `Edit ${form.city_name}`}
                            </h3>
                            <button onClick={closeForm} className="p-1 hover:bg-slate-100 rounded">
                                <X className="w-4 h-4" />
                            </button>
                        </header>
                        <div className="p-5 space-y-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1">City name *</label>
                                    <input
                                        type="text"
                                        value={form.city_name}
                                        onChange={(e) => {
                                            const next = { ...form, city_name: e.target.value };
                                            if (editing === "new" && !form.city_slug) next.city_slug = slugify(e.target.value);
                                            setForm(next);
                                        }}
                                        data-testid="admin-gbp-input-name"
                                        placeholder="e.g. Indore"
                                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1">City slug *</label>
                                    <input
                                        type="text"
                                        value={form.city_slug}
                                        disabled={editing !== "new"}
                                        onChange={(e) => setForm({ ...form, city_slug: slugify(e.target.value) })}
                                        data-testid="admin-gbp-input-slug"
                                        placeholder="e.g. indore"
                                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 outline-none disabled:bg-slate-50 disabled:text-slate-400 font-mono"
                                    />
                                    <p className="text-[10px] text-slate-400 mt-1">lowercase, kebab-case · used in URL routing</p>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-700 mb-1">Business URL</label>
                                <input
                                    type="url"
                                    value={form.business_url}
                                    onChange={(e) => setForm({ ...form, business_url: e.target.value })}
                                    data-testid="admin-gbp-input-business"
                                    placeholder="https://share.google/xxxxxx"
                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 outline-none"
                                />
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1">Google Maps URL</label>
                                    <input
                                        type="url"
                                        value={form.map_url}
                                        onChange={(e) => setForm({ ...form, map_url: e.target.value })}
                                        data-testid="admin-gbp-input-map"
                                        placeholder="https://maps.app.goo.gl/..."
                                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1">Review URL</label>
                                    <input
                                        type="url"
                                        value={form.review_url}
                                        onChange={(e) => setForm({ ...form, review_url: e.target.value })}
                                        data-testid="admin-gbp-input-review"
                                        placeholder="https://g.co/kgs/..."
                                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 outline-none"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-700 mb-1">Knowledge-Graph ID (kgmid)</label>
                                <input
                                    type="text"
                                    value={form.kgmid}
                                    onChange={(e) => setForm({ ...form, kgmid: e.target.value })}
                                    data-testid="admin-gbp-input-kgmid"
                                    placeholder="/g/11qn6140s2"
                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 outline-none font-mono"
                                />
                                <p className="text-[10px] text-slate-400 mt-1">Optional. Find it in your Google Maps listing URL after <code>kgmid=</code>. Improves Knowledge-Graph entity linking.</p>
                            </div>

                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.active}
                                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                    data-testid="admin-gbp-input-active"
                                    className="rounded text-[#1E3A8A]"
                                />
                                <span className="text-sm text-slate-700">
                                    <strong>Active</strong> · Show the "View us on Google" CTA + schema link on the {form.city_name || "city"} page
                                </span>
                            </label>
                        </div>
                        <footer className="flex items-center justify-end gap-2 px-5 py-3 bg-slate-50 border-t border-slate-200">
                            <button onClick={closeForm} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">
                                Cancel
                            </button>
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid="admin-gbp-save"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold rounded-lg text-sm disabled:opacity-50"
                            >
                                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                                {editing === "new" ? "Add profile" : "Save changes"}
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </section>
    );
}
